import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import { CartTransactionDetails } from "@/components/shared/CartTransactionDetails";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import { PromoCodeSection } from "@/components/shared/PromoCodeSection";
import Warning from "@/components/shared/Warning";
import { Box } from "@/components/ui/box";
import { Center } from "@/components/ui/center";
import { Divider } from "@/components/ui/divider";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useCheckout } from "@/hooks/useCheckout";
import { formatToDollars } from "@/utils/etc";
import { saveToStorage } from "@/utils/secureStore";
import * as Sentry from "@sentry/react-native";
import { useT } from "@transifex/react";
import { router, useLocalSearchParams, useNavigation } from "expo-router";
import { CheckoutFailReason } from "ipctg-inflow-types/InFlowEnums";
import { useEffect } from "react";
import { Platform } from "react-native";

export default function Cart() {
  const { isPurchasingPass, rateID, rateName } = useLocalSearchParams<{ isPurchasingPass: string; rateID: string; rateName: string }>();
  const navigation = useNavigation();
  const isPurchasingPassBoolean = isPurchasingPass === "true";
  const t = useT();

  const {
    isFetching,
    error,
    promoCode,
    setPromoCode,
    isPromoCodeLoading,
    isPromoCodeInvalid,
    isPromoApplied,
    handleApplyPromoCode,
    handleRemovePromoCode,
    loadingRemovePromoCode,
    handleFocusInput,
    transactionDetails,
    clearPromoCodeState,
  } = useCheckout({ rateID, isPurchasingPass: isPurchasingPassBoolean });

  useEffect(() => {
    const unsubscribe = navigation.addListener("blur", () => {
      clearPromoCodeState();
    });

    return () => unsubscribe();
  }, [navigation, clearPromoCodeState]);

  const handleCheckout = async () => {
    try {
      await saveToStorage("isPurchasingPass", isPurchasingPass);
      router.push({
        params: {
          noForm: transactionDetails?.amountDue ? "true" : "false",
          rateID,
          rateName,
          termsAndConditionsOKTimestamp: new Date().toISOString(),
        },
        pathname: "/purchase/payment-intent",
      });
    } catch (error) {
      console.error("Error saving isPurchasingPass:", error);
      Sentry.captureException(error, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
    }
  };

  if (isFetching) {
    return <CartScreenSkeleton />;
  }

  if (error) {
    return (
      <Screen className="p-10">
        <Center className="flex-1">
          {"failReason" in error && (error.failReason === CheckoutFailReason.SessionEnforced || error.failReason === CheckoutFailReason.SessionInViolation) ? (
            <Text>{t("This plate is currently in violation status. Parking activation is no longer available.")}</Text>
          ) : (
            <Text>{t("Error loading checkout details.")}</Text>
          )}
        </Center>
      </Screen>
    );
  }

  return (
    <Screen>
      <PageTitle title={t("Cart")} />
      {transactionDetails && <CartTransactionDetails transactionDetails={transactionDetails} isUserEnteredPromoApplied={isPromoApplied} />}

      <Divider className="bg-outline-300" />

      <PromoCodeSection
        isPromoCodeLoading={isPromoCodeLoading}
        promoCode={promoCode}
        onChangePromoCode={setPromoCode}
        onApplyPromoCode={handleApplyPromoCode}
        onRemovePromoCode={handleRemovePromoCode}
        isInvalid={isPromoCodeInvalid}
        isPromoApplied={isPromoApplied}
        onFocusInput={handleFocusInput}
        loadingRemovePromoCode={loadingRemovePromoCode}
        transactionDetails={transactionDetails}
      />

      <Divider className="bg-outline-300" />

      <HStack className="justify-between">
        <Text className="font-[700] text-[20px] text-typography-900">{t("Due Today:")}</Text>
        <Text className="font-[700] text-[20px] text-typography-900">{formatToDollars(transactionDetails?.amountDue || 0)}</Text>
      </HStack>

      <Divider className="bg-outline-300" />

      {isPurchasingPassBoolean && <Warning />}

      <HStack className="gap-[20px] w-full">
        <AppButton
          className="flex-1"
          icon={ArrowLeft}
          onPress={() => (router.canGoBack() ? router.back() : router.replace("/welcome"))}
          title={t("Back")}
          variant="outline"
        />
        <AppButton className="flex-1" icon={ArrowRight} iconPosition="after" onPress={handleCheckout} title={t("Checkout")} />
      </HStack>
    </Screen>
  );
}

function CartScreenSkeleton() {
  return (
    <Screen>
      <Box className="items-center">
        <SkeletonText className="h-[30px] w-[100px] rounded-md" />
      </Box>

      <VStack className="space-y-4">
        <HStack className="justify-between">
          <SkeletonText className="h-[16px] w-[100px] rounded-md" />
          <SkeletonText className="h-[16px] w-[50px] rounded-md" />
        </HStack>

        <HStack className="justify-between">
          <SkeletonText className="h-[16px] w-[150px] rounded-md" />
          <SkeletonText className="h-[16px] w-[50px] rounded-md" />
        </HStack>

        <HStack className="justify-between">
          <SkeletonText className="h-[16px] w-[120px] rounded-md" />
          <SkeletonText className="h-[16px] w-[50px] rounded-md" />
        </HStack>
      </VStack>

      <Divider className="bg-outline-300" />

      <VStack>
        <SkeletonText className="h-[14px] w-[100px] rounded-md my-4" />
        <HStack className="justify-between gap-[10px]">
          <Skeleton className="h-[40px] w-[70%] rounded-md" />
          <Skeleton className="h-[40px] w-[30%] rounded-md" />
        </HStack>
      </VStack>

      <Divider className="bg-outline-300" />

      <HStack className="justify-between gap-[10px]">
        <SkeletonText className="h-[20px] w-[110px] rounded-md" />
        <SkeletonText className="h-[20px] w-[71px] rounded-md" />
      </HStack>

      <Divider className="bg-outline-300" />

      <HStack className="gap-[20px] w-full">
        <Skeleton className="h-[52px] flex-1 rounded-md" />
        <Skeleton className="h-[52px] flex-1 rounded-md" />
      </HStack>
    </Screen>
  );
}
